import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { IVehicleRegistrationFormValues } from 'features/Vehicles/types'
import { getSchemaAssociations } from 'utils/schema'
import { GTVehicleRegistrationCategoryInput } from 'gql/types.generated'

const associatedKeys = ['expirationDate', 'state', 'category', 'document']

const messages = defineMessages({
  required: {
    id: 'vehicleData.new.form.registration.required',
    defaultMessage: 'This field is required'
  }
})

const registrationShouldBeRequired = ({
  isPermanent,
  expirationDate,
  state,
  category,
  document
}: IVehicleRegistrationFormValues['registration']): boolean => {
  return (
    isPermanent ||
    Boolean(expirationDate) ||
    Boolean(state?.code) ||
    Boolean(category) ||
    Boolean(document?.file) ||
    Boolean(document?.path)
  )
}

export const schema = (intl: IntlShape): yup.SchemaOf<IVehicleRegistrationFormValues> => {
  const validations = {
    registration: yup
      .object()
      .shape(
        {
          expirationDate: yup.date().when('isPermanent', {
            is: (isPermanent: IVehicleRegistrationFormValues['registration']['isPermanent']) => isPermanent,
            then: yup.date().optional(),
            otherwise: yup.date().when(['state', 'category', 'document'], {
              is: (
                state: IVehicleRegistrationFormValues['registration']['state'],
                category: IVehicleRegistrationFormValues['registration']['category'],
                document: IVehicleRegistrationFormValues['registration']['document']
              ) => {
                return registrationShouldBeRequired({
                  isPermanent: false,
                  expirationDate: undefined,
                  state,
                  category,
                  document
                })
              },
              then: yup.date().required(intl.formatMessage(messages.required))
            })
          }),
          isPermanent: yup.boolean().optional(),
          state: yup
            .object()
            .shape({
              name: yup.string().required(),
              code: yup.string().required()
            })
            .nullable()
            .when(['expirationDate', 'category', 'document', 'isPermanent'], {
              is: (
                expirationDate: IVehicleRegistrationFormValues['registration']['expirationDate'],
                category: IVehicleRegistrationFormValues['registration']['category'],
                document: IVehicleRegistrationFormValues['registration']['document'],
                isPermanent: IVehicleRegistrationFormValues['registration']['isPermanent']
              ) => {
                return (
                  registrationShouldBeRequired({
                    isPermanent,
                    expirationDate,
                    state: undefined,
                    category,
                    document
                  }) && category !== GTVehicleRegistrationCategoryInput.Federal
                )
              },
              then: yup
                .object()
                .shape({
                  name: yup.string().required(intl.formatMessage(messages.required)),
                  code: yup.string().required(intl.formatMessage(messages.required))
                })
                .nullable()
                .required(intl.formatMessage(messages.required))
            }),
          category: yup.mixed().when(['expirationDate', 'state', 'document', 'isPermanent'], {
            is: (
              expirationDate: IVehicleRegistrationFormValues['registration']['expirationDate'],
              state: IVehicleRegistrationFormValues['registration']['state'],
              document: IVehicleRegistrationFormValues['registration']['document'],
              isPermanent: IVehicleRegistrationFormValues['registration']['isPermanent']
            ) => {
              return registrationShouldBeRequired({
                isPermanent,
                expirationDate,
                state,
                category: undefined,
                document
              })
            },
            then: yup.mixed().required(intl.formatMessage(messages.required))
          }),
          document: yup
            .object()
            .shape({
              name: yup.string(),
              file: yup.mixed(),
              path: yup.string()
            })
            .nullable()
        },
        getSchemaAssociations(associatedKeys)
      )
      .nullable()
  }

  return yup.object().shape(validations)
}
